import { useEffect, useState } from 'react';
import { Search, Loader2, ShieldOff, ShieldCheck, Crown, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/adminApi';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'premium', label: 'Premium' },
  { id: 'banned', label: 'Banned' },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'all') params.set('status', tab);
      if (search) params.set('search', search);
      params.set('limit', '50');
      const data = await adminApi(`/admin/users?${params.toString()}`);
      setUsers(data.users || []);
      setStats(data.stats || {});
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search]);

  async function action(userId, type, payload = {}) {
    try {
      let endpoint, body;
      if (type === 'ban') { endpoint = `/admin/users/${userId}/ban`; body = { reason: payload.reason || 'Violation of terms' }; }
      else if (type === 'unban') { endpoint = `/admin/users/${userId}/unban`; body = {}; }
      else if (type === 'premium') { endpoint = `/admin/users/${userId}/set-premium`; body = { is_premium: payload.is_premium, days: payload.days || 30 }; }
      else if (type === 'verify') { endpoint = `/admin/users/${userId}/verify`; body = { badge_type: 'photo_verified' }; }
      else if (type === 'delete') { 
        if (!window.confirm('Permanently delete this user and all data? This cannot be undone.')) return;
        await adminApi(`/admin/users/${userId}`, { method: 'DELETE' });
        toast.success('User deleted');
        setSelected(null); load(); return;
      }
      await adminApi(endpoint, { method: 'POST', body: JSON.stringify(body) });
      toast.success(`Action: ${type}`);
      load();
      if (selected) {
        const refreshed = await adminApi(`/admin/users/${userId}`);
        setSelected(refreshed);
      }
    } catch (err) {
      toast.error(err.message || 'Action failed');
    }
  }

  async function viewUser(uid) {
    try {
      const data = await adminApi(`/admin/users/${uid}`);
      setSelected(data);
    } catch (err) { toast.error(err.message); }
  }

  return (
    <div className="p-8" data-testid="admin-users-page">
      <h1 className="text-3xl font-serif font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Users</h1>
      <p className="text-white/50 text-sm mb-5">Manage users — actions affect web AND mobile users equally.</p>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 flex items-center bg-[#1A1820] border border-white/10 rounded-lg px-4 py-2.5">
          <Search className="w-4 h-4 text-white/40 mr-2" />
          <input data-testid="admin-users-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, city..." className="flex-1 bg-transparent outline-none text-white text-sm placeholder-white/30" />
        </div>
      </div>

      <div className="flex gap-2 mb-4 bg-[#1A1820] border border-white/10 rounded-full p-1 w-fit">
        {TABS.map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-users-tab-${id}`} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${tab === id ? 'bg-[#C8553D] text-white' : 'text-white/60'}`}>
            {label} {stats[id] !== undefined && `(${stats[id]})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>
      ) : (
        <div className="bg-[#1A1820] border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full" data-testid="admin-users-table">
            <thead className="bg-white/5">
              <tr className="text-xs uppercase tracking-wider text-white/50">
                <th className="text-left px-4 py-3 font-semibold">User</th>
                <th className="text-left px-4 py-3 font-semibold">City</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Reports</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#FFE5DD] overflow-hidden flex-shrink-0">
                        {u.photos?.[0] ? <img src={u.photos[0]} alt={u.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-[#C8553D]">{u.name?.charAt(0) || '?'}</div>}
                      </div>
                      <div>
                        <button onClick={() => viewUser(u.id)} className="font-semibold text-white text-sm hover:text-[#E07A5F] text-left">{u.name || '(no name)'}</button>
                        <p className="text-white/40 text-xs">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-white/70">{u.city || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {u.is_banned && <span className="px-2 py-0.5 bg-[#E76F51]/30 text-[#E76F51] text-[10px] font-bold rounded">BANNED</span>}
                      {u.is_premium && <span className="px-2 py-0.5 bg-[#E9C46A]/30 text-[#E9C46A] text-[10px] font-bold rounded">PREMIUM</span>}
                      {u.email_verified && <span className="px-2 py-0.5 bg-[#2A9D8F]/30 text-[#2A9D8F] text-[10px] font-bold rounded">VERIFIED</span>}
                      {u.profile_complete && <span className="px-2 py-0.5 bg-white/10 text-white/60 text-[10px] font-bold rounded">COMPLETE</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-white/70">{u.report_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => viewUser(u.id)} data-testid={`admin-user-view-${u.id}`} className="text-xs font-bold text-[#E07A5F] hover:bg-white/5 px-2 py-1 rounded">View</button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-white/40 text-sm">No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-end" data-testid="admin-user-drawer" onClick={() => setSelected(null)}>
          <div className="bg-[#1A1820] border-l border-white/10 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-serif font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>{selected.user.name}, {selected.user.age}</h2>
                <p className="text-white/40 text-xs">{selected.user.email}</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="admin-user-drawer-close" className="text-white/60"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {selected.user.photos?.[0] && <img src={selected.user.photos[0]} alt="" className="w-full aspect-square rounded-xl object-cover" />}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Stat k="City" v={selected.user.city} />
                <Stat k="Country" v={selected.user.country_of_origin} />
                <Stat k="Matches" v={selected.stats?.matches} />
                <Stat k="Messages" v={selected.stats?.messages_sent} />
                <Stat k="Likes given" v={selected.stats?.likes_given} />
                <Stat k="Likes received" v={selected.stats?.likes_received} />
                <Stat k="Reports against" v={selected.stats?.reports_against} />
                <Stat k="Trust score" v={selected.user.trust_score} />
              </div>
              {selected.user.bio && <div><p className="text-[10px] uppercase text-white/50 font-bold mb-1">Bio</p><p className="text-sm text-white/80">{selected.user.bio}</p></div>}
              <div className="space-y-2 pt-3 border-t border-white/10">
                {selected.user.is_banned ? (
                  <button onClick={() => action(selected.user.id, 'unban')} data-testid="admin-user-unban" className="w-full flex items-center justify-center gap-2 bg-[#2A9D8F]/20 hover:bg-[#2A9D8F]/40 text-[#2A9D8F] font-bold py-2.5 rounded-lg text-sm"><ShieldCheck className="w-4 h-4" /> Unban User</button>
                ) : (
                  <button onClick={() => action(selected.user.id, 'ban')} data-testid="admin-user-ban" className="w-full flex items-center justify-center gap-2 bg-[#E76F51]/20 hover:bg-[#E76F51]/40 text-[#E76F51] font-bold py-2.5 rounded-lg text-sm"><ShieldOff className="w-4 h-4" /> Ban User</button>
                )}
                {selected.user.is_premium ? (
                  <button onClick={() => action(selected.user.id, 'premium', { is_premium: false })} data-testid="admin-user-revoke-premium" className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white/70 font-bold py-2.5 rounded-lg text-sm"><Crown className="w-4 h-4" /> Revoke Premium</button>
                ) : (
                  <button onClick={() => action(selected.user.id, 'premium', { is_premium: true, days: 30 })} data-testid="admin-user-grant-premium" className="w-full flex items-center justify-center gap-2 bg-[#E9C46A]/20 hover:bg-[#E9C46A]/40 text-[#E9C46A] font-bold py-2.5 rounded-lg text-sm"><Crown className="w-4 h-4" /> Grant Premium (30 days)</button>
                )}
                <button onClick={() => action(selected.user.id, 'verify')} data-testid="admin-user-verify" className="w-full flex items-center justify-center gap-2 bg-[#457B9D]/20 hover:bg-[#457B9D]/40 text-[#457B9D] font-bold py-2.5 rounded-lg text-sm"><ShieldCheck className="w-4 h-4" /> Mark Photo Verified</button>
                <button onClick={() => action(selected.user.id, 'delete')} data-testid="admin-user-delete" className="w-full flex items-center justify-center gap-2 bg-[#E76F51]/10 hover:bg-[#E76F51]/30 text-[#E76F51] font-bold py-2.5 rounded-lg text-sm"><Trash2 className="w-4 h-4" /> Delete Account</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return <div><p className="text-[10px] uppercase text-white/50 font-bold mb-0.5">{k}</p><p className="text-sm text-white">{v ?? '—'}</p></div>;
}
